import { DashboardBaseContent } from '../../constants/intl/dashboard';
import { RedirectLink, RedirectOptions } from '../../types/redirectLinks';

export function linkRedirect(
  data: DashboardBaseContent,
  redirectLinks: RedirectLink[]
) {
  const redirectUrl = getRedirectOption(data.id!, redirectLinks);

  if (redirectUrl) {
    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
  } else {
    console.warn('no matching redirect link found for:', data.id);
  }
}

type DatabaseType = 'mysql' | 'mssql' | 'postgres' | 'default';
type OperatingSystem = 'windows' | 'linux' | 'macos';

interface OSRedirectMapping {
  [key: string]: RedirectOptions;
}

const DATABASE_REDIRECT_MAPPING: Record<DatabaseType, OSRedirectMapping> = {
  mysql: {
    windows: RedirectOptions.WINDOWS,
    linux: RedirectOptions.LINUX,
    macos: RedirectOptions.MACOS,
  },
  mssql: {
    windows: RedirectOptions.MSSQL_SCRIPTER_WINDOWS,
    linux: RedirectOptions.MSSQL_SCRIPTER_LINUX,
    macos: RedirectOptions.MSSQL_SCRIPTER_MACOS,
  },
  // The PostgreSQL tools need Python (and Docker), not database client tools.
  postgres: {
    windows: RedirectOptions.WINDOWS,
    linux: RedirectOptions.LINUX,
    macos: RedirectOptions.MACOS,
  },
  default: {
    windows: RedirectOptions.WINDOWS,
    linux: RedirectOptions.LINUX,
    macos: RedirectOptions.MACOS,
  },
};

function detectOperatingSystem(panelString: string): OperatingSystem | null {
  const lowerString = panelString.toLowerCase();

  // For python-related panels, don't detect OS-specific behavior
  if (lowerString.includes('python')) return null;
  
  if (lowerString.includes('windows')) return 'windows';
  if (lowerString.includes('linux')) return 'linux';
  if (lowerString.includes('mac') || lowerString.includes('macos'))
    return 'macos';

  return null;
}

function detectDatabaseType(panelString: string): DatabaseType {
  const lowerString = panelString.toLowerCase();

  if (lowerString.includes('a_mysql')) return 'mysql';
  if (lowerString.includes('a_mssql')) return 'mssql';
  if (lowerString.includes('a_postgres')) return 'postgres';

  return 'default';
}

function findRedirectLink(
  redirectLinks: RedirectLink[],
  key: RedirectOptions
): string {
  return redirectLinks.find((link) => link.key == key)?.value || '';
}

export function getRedirectOption(
  panelString: string,
  redirectLinks: RedirectLink[]
): string {
  const lowerCaseString = panelString.toLowerCase();

  if (lowerCaseString.includes('awscliinstalldoc')) {
    return findRedirectLink(redirectLinks, RedirectOptions.AWS_CLI_INSTALL);
  }

  if (lowerCaseString.includes('awsclis3configdoc')) {
    return findRedirectLink(redirectLinks, RedirectOptions.AWS_CLI_S3_CONFIG);
  }

  const os = detectOperatingSystem(panelString);
  const dbType = detectDatabaseType(panelString);

  if (os) {
    const redirectKey = DATABASE_REDIRECT_MAPPING[dbType][os];
    return findRedirectLink(redirectLinks, redirectKey);
  }

  // Default behavior for other cases
  for (const link of redirectLinks) {
    if (lowerCaseString.replace('mac', 'macos').includes(link.key)) {
      return link.value;
    }
  }

  return '';
}
